import axios from 'axios'
import {EIMZO_API_KEY, EIMZO_HOST, EIMZO_URL} from '@/settings/index'

class CertificateCertkey {
    get type() {
        return this._type;
    }

    get disk() {
        return this._disk;
    }

    get path() {
        return this._path;
    }

    get name() {
        return this._name;
    }

    get serialNumber() {
        return this._serialNumber;
    }

    get validFromDate() {
        return this._validFromDate;
    }

    get validEndDate() {
        return this._validEndDate;
    }

    get innNumber() {
        return this._innNumber;
    }

    get companyName() {
        return this._companyName;
    }

    get issuedPerson() {
        return this._issuedPerson;
    }

    get alias() {
        return this._alias;
    }

    constructor(eimzoCertificateObject) {
        const subjectSeparatedValues = eimzoCertificateObject.subjectName.split(',');
        let innNumber = this._getValueFromSeparatedValuesArray(subjectSeparatedValues, 'INITIALS', null);
        if (innNumber === null) {
            innNumber = this._getValueFromSeparatedValuesArray(subjectSeparatedValues, 'INN', null);
            if (innNumber === null)
                innNumber = this._getValueFromSeparatedValuesArray(subjectSeparatedValues, 'UID', '');
        }

        this._type = 'cer';
        this._disk = eimzoCertificateObject.disk;
        this._path = eimzoCertificateObject.path;
        this._name = eimzoCertificateObject.name;
        this._serialNumber = eimzoCertificateObject.serialNumber;
        this._validFromDate = new Date(eimzoCertificateObject.validFrom);
        this._validEndDate = new Date(eimzoCertificateObject.validTo);
        this._innNumber = innNumber;
        this._companyName = this._getValueFromSeparatedValuesArray(subjectSeparatedValues, 'O', '');
        this._issuedPerson = this._getValueFromSeparatedValuesArray(subjectSeparatedValues, 'CN', '');
        this._alias = null;
    }

    _getValueFromSeparatedValuesArray(values, key, defaultValue) {
        const keyInUpperCase = `${key.toUpperCase()}=`;

        for (let i = 0; i < values.length; i++) {
            const value = values[i];
            if (value.length >= keyInUpperCase.length)
                if (value.substr(0, keyInUpperCase.length).toUpperCase() === keyInUpperCase)
                    return value.substr(keyInUpperCase.length);
        }

        if (typeof (defaultValue) === 'undefined')
            defaultValue = null;

        return defaultValue;
    }
}

class CertificatePfx {
    get type() {
        return this._type;
    }

    get disk() {
        return this._disk;
    }

    get path() {
        return this._path;
    }

    get name() {
        return this._name;
    }

    get serialNumber() {
        return this._serialNumber;
    }

    get validFromDate() {
        return this._validFromDate;
    }

    get validEndDate() {
        return this._validEndDate;
    }

    get innNumber() {
        return this._innNumber;
    }

    get companyName() {
        return this._companyName;
    }

    get issuedPerson() {
        return this._issuedPerson;
    }

    get alias() {
        return this._alias;
    }

    constructor(eimzoCertificateObject) {
        const aliasValues = eimzoCertificateObject.alias.split(',');
        let innNumber = this._getValueFromSeparatedValuesArray(aliasValues, '1.2.860.3.16.1.1', null);
        if (innNumber === null) {
            innNumber = this._getValueFromSeparatedValuesArray(aliasValues, 'INN', null);
            if (innNumber === null)
                innNumber = this._getValueFromSeparatedValuesArray(aliasValues, 'UID', '');
        }

        const todaysLastMoment = new Date();
        todaysLastMoment.setHours(23);
        todaysLastMoment.setMinutes(59);
        todaysLastMoment.setSeconds(59);
        todaysLastMoment.setMilliseconds(999);

        let validFrom = this._getValueFromSeparatedValuesArray(aliasValues, 'validfrom', null);
        if (validFrom === null || validFrom === undefined)
            validFrom = todaysLastMoment;
        else
            validFrom = new Date(validFrom.split('.').join('-'));

        let validTo = this._getValueFromSeparatedValuesArray(aliasValues, 'validto', null);
        if (validTo === null || validTo === undefined)
            validTo = todaysLastMoment;
        else
            validTo = new Date(validTo.split('.').join('-'));

        this._type = 'pfx';
        this._disk = eimzoCertificateObject.disk;
        this._path = eimzoCertificateObject.path;
        this._name = eimzoCertificateObject.name;
        this._serialNumber = this._getValueFromSeparatedValuesArray(aliasValues, 'serialnumber', '');
        this._validFromDate = new Date(validFrom);
        this._validEndDate = new Date(validTo);
        this._innNumber = innNumber;
        this._companyName = this._getValueFromSeparatedValuesArray(aliasValues, 'o', '');
        this._issuedPerson = this._getValueFromSeparatedValuesArray(aliasValues, 'cn', '');
        this._alias = eimzoCertificateObject.alias;
    }

    _getValueFromSeparatedValuesArray(values, key, defaultValue) {
        const keyInUpperCase = `${key.toUpperCase()}=`;

        for (let i = 0; i < values.length; i++) {
            const value = values[i];
            if (value.length >= keyInUpperCase.length)
                if (value.substr(0, keyInUpperCase.length).toUpperCase() === keyInUpperCase)
                    return value.substr(keyInUpperCase.length);
        }

        if (typeof (defaultValue) === 'undefined')
            defaultValue = null;

        return defaultValue;
    }
}

class ActiveCertificate {
    constructor(certificate, keyId, activateTime) {
        this._certificate = certificate;
        this._activateTime = activateTime;
        this._keyId = keyId;
    }

    get certificate() {
        return this._certificate;
    }

    get activateTime() {
        return this._activateTime;
    }

    get keyId() {
        return this._keyId;
    }
}

class SignResult {
    constructor(pkcs7, signatureHex, serialNumber) {
        this._pkcs7 = pkcs7;
        this._signatureHex = signatureHex;
        this._serialNumber = serialNumber;
    }

    get pkcs7() {
        return this._pkcs7;
    }

    get signatureHex() {
        return this._signatureHex;
    }

    get serialNumber() {
        return this._serialNumber;
    }
}

class AttachTimestampResult {
    constructor(pkcs7, signerSeriaNumber) {
        this._pkcs7 = pkcs7;
        this._signerSeriaNumber = signerSeriaNumber;
    }

    get pkcs7() {
        return this._pkcs7;
    }

    get signerSeriaNumber() {
        return this._signerSeriaNumber;
    }
}

class Eimzo {
    constructor(url, hostname, apiKey) {
        this.settings = {
            _url: url,
            _hostname: hostname,
            _apiKey: apiKey
        };

        let fromCharCode = String.fromCharCode;
        let cb_utob = function (c) {
            if (c.length < 2) {
                let cc = c.charCodeAt(0);
                return cc < 0x80 ? c
                    : cc < 0x800 ? (fromCharCode(0xc0 | (cc >>> 6))
                            + fromCharCode(0x80 | (cc & 0x3f)))
                        : (fromCharCode(0xe0 | ((cc >>> 12) & 0x0f))
                            + fromCharCode(0x80 | ((cc >>> 6) & 0x3f))
                            + fromCharCode(0x80 | (cc & 0x3f)));
            } else {
                let cc = 0x10000
                    + (c.charCodeAt(0) - 0xD800) * 0x400
                    + (c.charCodeAt(1) - 0xDC00);
                return (fromCharCode(0xf0 | ((cc >>> 18) & 0x07))
                    + fromCharCode(0x80 | ((cc >>> 12) & 0x3f))
                    + fromCharCode(0x80 | ((cc >>> 6) & 0x3f))
                    + fromCharCode(0x80 | (cc & 0x3f)));
            }
        };
        let re_utob = /[\uD800-\uDBFF][\uDC00-\uDFFFF]|[^\x00-\x7F]/g;
        let utob = function (u) {
            return u.replace(re_utob, cb_utob);
        };
        let _encode = function (u) {
            return btoa(utob(u))
        };
        let base64 = {
            encode: function (u, urisafe) {
                return !urisafe
                    ? _encode(u)
                    : _encode(u).replace(/[+\/]/g, function (m0) {
                        return m0 === '+' ? '-' : '_';
                    }).replace(/=/g, '');
            }
        }

        this._helpers = {
            base64: base64
            /*base64: {
                _keyStr: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=',
                encode: function (e) {
                    let t = '';
                    let n, r, i, s, o, u, a;
                    let f = 0;
                    e = this._utf8_encode(e);
                    while (f < e.length) {
                        n = e.charCodeAt(f++);
                        r = e.charCodeAt(f++);
                        i = e.charCodeAt(f++);
                        s = n >> 2;
                        o = (n & 3) << 4 | r >> 4;
                        u = (r & 15) << 2 | i >> 6;
                        a = i & 63;
                        if (isNaN(r)) {
                            u = a = 64;
                        } else if (isNaN(i)) {
                            a = 64;
                        }
                        t = t +
                            this._keyStr.charAt(s) +
                            this._keyStr.charAt(o) +
                            this._keyStr.charAt(u) +
                            this._keyStr.charAt(a);
                    }
                    return t;
                },
                _utf8_encode: function (e) {
                    e = e.replace(/rn/g, 'n');
                    let t = '';
                    for (let n = 0; n < e.length; n++) {
                        const r = e.charCodeAt(n);
                        if (r < 128) {
                            t += String.fromCharCode(r);
                        } else if (r > 127 && r < 2048) {
                            t += String.fromCharCode(r >> 6 | 192);
                            t += String.fromCharCode(r & 63 | 128);
                        } else {
                            t += String.fromCharCode(r >> 12 | 224);
                            t += String.fromCharCode(r >> 6 & 63 | 128);
                            t += String.fromCharCode(r & 63 | 128);
                        }
                    }
                    return t;
                }
            }*/
        };

        this._isWebSocketEnabled = !!window.WebSocket;
        this._isInitialized = false;

        this._certificates = [];
        this._activeCertificates = [];

        const self = this;
        self._init()
            .then(() => {
                if (self._isInitialized)
                    self._loadCertificates();
            }, () => {
            });
    }

    get certificates() {
        return this._certificates;
    }

    get activeCertificates() {
        return this._activeCertificates;
    }

    activateCertificate(certificate) {
        let self = this;

        return new Promise((resolve, reject) => {
            const data = {
                plugin: certificate.type === 'pfx' ? 'pfx' : 'certkey',
                name: 'load_key',
                arguments: [
                    certificate.disk,
                    certificate.path,
                    certificate.name,
                    (certificate.type === 'pfx' ? certificate.alias : certificate.serialNumber)
                ]
            };
            self._makeRequest(data)
                .then((loadKeyResponse) => {
                    let activatedCertificate = null;
                    if (loadKeyResponse.success) {
                        activatedCertificate = new ActiveCertificate(certificate, loadKeyResponse.keyId, new Date());
                        this._activeCertificates.push(activatedCertificate);
                        resolve(activatedCertificate);
                    } else {
                        reject();
                    }
                }, reject);
        });
    }

    createPkcs7(hashCode, activeCertificate) {
        const self = this;
        const hashCodeBase64 = self._helpers.base64.encode(hashCode);

        return new Promise((resolve, reject) => {
            const data = {
                plugin: "pkcs7",
                name: "create_pkcs7",
                arguments: [
                    hashCodeBase64,
                    activeCertificate.keyId,
                    "no"
                ]
            };

            self._makeRequest(data)
                .then((createPkcs7Response) => {
                    if (createPkcs7Response.success) {
                        const signResult = new SignResult(createPkcs7Response.pkcs7_64
                            , createPkcs7Response.signature_hex
                            , createPkcs7Response.signer_serial_number
                        );
                        resolve(signResult);
                    } else {
                        reject();
                    }
                }, reject);
        });
    }

    attachPkcs7(pkcs7, activeCertificate) {
        const self = this;

        return new Promise((resolve, reject) => {
            const data = {
                plugin: "pkcs7",
                name: "append_pkcs7_attached",
                arguments: [
                    pkcs7,
                    activeCertificate.keyId
                ]
            };

            self._makeRequest(data)
                .then((attackPkcs7Response) => {
                    if (attackPkcs7Response.success) {
                        const signResult = new SignResult(attackPkcs7Response.pkcs7_64
                            , attackPkcs7Response.signature_hex
                            , attackPkcs7Response.signer_serial_number
                        );
                        resolve(signResult);
                    } else {
                        reject();
                    }
                }, reject);
        });
    }

    attachTimestamp(pkcs7, activeCertificate, timestamp) {
        const self = this;

        return new Promise((resolve, reject) => {
            const data = {
                plugin: "pkcs7",
                name: "attach_timestamp_token_pkcs7",
                arguments: [
                    pkcs7,
                    activeCertificate.certificate.serialNumber,
                    timestamp
                ]
            };

            self._makeRequest(data)
                .then((attachTimeStampResponse) => {
                    if (attachTimeStampResponse.success) {
                        const attachTimestampResult = new AttachTimestampResult(attachTimeStampResponse.pkcs7_64, activeCertificate.certificate.serialNumber);
                        resolve(attachTimestampResult);
                    } else {
                        reject();
                    }
                }, reject);
        });
    }

    sign(fakturaHashCode, roamingHashCode, activatedCertificate, getTimestampFn) {
        const eimzo = this;
        return new Promise((resolve, reject) => {
            eimzo.createPkcs7(fakturaHashCode, activatedCertificate)
                .then(signResult => {
                    eimzo.createPkcs7(roamingHashCode, activatedCertificate)
                        .then(roamingCreatePkcs7SignResult => {
                            getTimestampFn(roamingCreatePkcs7SignResult.signatureHex)
                                .then(timestamp => {
                                    eimzo.attachTimestamp(roamingCreatePkcs7SignResult.pkcs7, activatedCertificate, timestamp)
                                        .then(attachTimestampResult => {
                                            resolve([signResult, attachTimestampResult]);
                                        }, reject);
                                }, reject);
                        }, reject);
                }, reject);
        });
    }

    attachTimestampFromServer(signature) {
        return axios.post('https://app.hippo.uz/eimzo/pkcs7/attach-timestamp', {signature: signature});
    }

    signByAttach(fakturaHashCode, roamingHashCode, activatedCertificate, getTimestampFn) {
        const eimzo = this;
        return new Promise((resolve, reject) => {
            eimzo.createPkcs7(fakturaHashCode, activatedCertificate)
                .then(signResult => {
                    eimzo.attachPkcs7(roamingHashCode, activatedCertificate)
                        .then(roamingCreatePkcs7SignResult => {
                            eimzo.attachTimestampFromServer(roamingCreatePkcs7SignResult.pkcs7)
                                .then(({data: {pkcs7b64: roamingSignature, status: status}}) => {
                                    if (status === 1) {
                                        resolve([signResult, {
                                            pkcs7: roamingSignature,
                                            signerSeriaNumber: roamingCreatePkcs7SignResult.serialNumber
                                        }]);
                                    } else {
                                        reject();
                                    }
                                }, reject)
                        }, reject);
                }, reject);
        })
    }

    _init() {
        var self = this;
        return new Promise((resolve, reject) => {
            var data = {
                name: 'apikey',
                arguments: [self.settings._hostname, self.settings._apiKey]
            };
            self._makeRequest(data).then((response) => {
                self._isInitialized = response.success;
                resolve();
            }, () => {
                reject();
            });
        });
    }

    _loadDisks() {
        var self = this;
        return new Promise((resolve, reject) => {
            self._makeRequest({plugin: "pfx", name: "list_disks"})
                .then((response) => {
                    resolve(response);
                }, reject);
        });
    }

    _loadCertificates() {
        var self = this;
        return new Promise((resolve, reject) => {
            self._certificates = [];
            var promises = [];

            self._loadDisks()
                .then((response) => {
                    if (response.success) {
                        for (var diskIndex = 0; diskIndex < response.disks.length; diskIndex++) {
                            // pfx
                            var loadDiskPfxCertificatesPromise = self._makeRequest({
                                plugin: "pfx",
                                name: "list_certificates",
                                arguments: [response.disks[diskIndex]]
                            });

                            promises.push(loadDiskPfxCertificatesPromise);

                            loadDiskPfxCertificatesPromise.then((loadPfxCertificatesResponse) => {
                                for (var pfxCerficiateIndex = 0;
                                     pfxCerficiateIndex < loadPfxCertificatesResponse.certificates.length;
                                     pfxCerficiateIndex++) {
                                    var pfxCertificate = new CertificatePfx(loadPfxCertificatesResponse.certificates[pfxCerficiateIndex]);

                                    self._certificates.push(pfxCertificate);
                                }
                            });


                            // certkey
                            var loadDiskCertkeyCertificatesPromise = self._makeRequest({
                                plugin: "certkey",
                                name: "list_certificates",
                                arguments: [response.disks[diskIndex]]
                            });

                            promises.push(loadDiskCertkeyCertificatesPromise);

                            loadDiskCertkeyCertificatesPromise.then((loadCertkeyCertificatesResponse) => {
                                for (var pfxCerficiateIndex = 0;
                                     pfxCerficiateIndex < loadCertkeyCertificatesResponse.certificates.length;
                                     pfxCerficiateIndex++) {
                                    var certKeyCertificate = new CertificateCertkey(loadCertkeyCertificatesResponse.certificates[pfxCerficiateIndex]);

                                    self._certificates.push(certKeyCertificate);
                                }
                            });
                        }
                    }

                    Promise.all(promises).then(resolve, reject);
                }, reject);
        });
    }

    _makeRequest(data) {
        const self = this;
        return new Promise((resolve, reject) => {
            let socket;
            try {
                socket = new WebSocket(self.settings._url);
                socket.onopen = () => {
                    socket.send(JSON.stringify(data));
                };
                socket.onmessage = (event) => {
                    socket.close();
                    let response = JSON.parse(event.data);
                    resolve(response);
                };
                socket.onerror = () => {
                    socket.close();
                    reject();
                };
            } catch (e) {
                reject();
            }
        });
    }
}

export default new Eimzo(EIMZO_URL, EIMZO_HOST, EIMZO_API_KEY);